import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import StableSection from './StableSection';
import PanelSection from './PanelSection';
import PostSection from './PostSection';
import ResultsSection from './ResultsSection';
import useStyles from './styles';
import AccessorySection from './AccessorySection';
import ColorSection from './ColorSection';
import TypeSection from './TypeSection';
import HeightSection from './HeightSection';
import CheckboxSection from './CheckboxSection';
import CornerPostSection from './CornerPostSection';
import getTreeStructureSettings from '../../../utils/helper_functions/getTreeStructureSettings';
import Loader from '../../commons/Loader';
import { inputDataCustom } from '../../../utils/custom_prop_types/customPropTypes';
import ResetButton from './ResetButton';

export default function OrderForm({
  inputData,
  setInputDataToSend,
  panels,
  posts,
  cornerPosts,
  fetchProductsBySection,
  calculateProductsQuantity,
  setValidation,
  setAddedProducts,
  fetchFilters,
  necessaryProductsQuantity,
}) {
  const [panelsInput, setPanelsInput] = useState('');
  const [postsInput, setPostsInput] = useState('');
  const classes = useStyles();

  const handleCalculateButtonSubmit = (event) => {
    event.preventDefault();

    setInputDataToSend('accessories', getTreeStructureSettings(inputData.accessoriesFlat));
    setValidation('form', []);

    calculateProductsQuantity(
      Number(inputData.distance),
      inputData.panelIRI,
      inputData.postIRI,
      inputData.cornerPostIRI,
      Number(inputData.corners),
      Number(inputData.openings),
      getTreeStructureSettings(inputData.accessoriesFlat) || inputData.accessoriesFlat,
      inputData.doublePost,
    );
  };

  const isInArray = (array, testedValue, compareValue) => array?.['hydra:member']
    ?.filter((element) => element[testedValue] === compareValue);

  const handlePanelSelectChange = (panel) => {
    const selectedPanel = panel?.value || '';
    const panelIRI = panel?.iri || '';

    setPanelsInput(panel?.description || '');
    setInputDataToSend('panelIRI', panelIRI);
    setInputDataToSend('panelGroupNumber', selectedPanel);

    fetchProductsBySection({
      section: 'post',
      panelGroup: selectedPanel,
      color: inputData.color,
    });
  };

  const handlePostSelectChange = (post) => {
    const selectedProductGroup = post?.value || '';
    const postIRI = post?.iri || '';

    setPostsInput(post?.description || '');
    setInputDataToSend('postGroupNumber', selectedProductGroup);
    setInputDataToSend('postIRI', postIRI);

    if (post) {
      fetchProductsBySection({
        section: 'corner_post',
        panelGroup: inputData.panelGroupNumber,
        postGroup: selectedProductGroup,
        color: inputData.color,
      });
    }
    fetchProductsBySection({
      section: 'accessory',
      panelGroup: inputData.panelGroupNumber,
      postGroup: selectedProductGroup,
      color: inputData.color,
    });

    fetchProductsBySection({
      section: 'corner_accessory',
      panelGroup: inputData.panelGroupNumber,
      postGroup: selectedProductGroup,
      color: inputData.color,
    });
  };

  useEffect(() => {
    fetchFilters(inputData);
    fetchProductsBySection({
      section: 'panel',
      color: inputData.color,
      type: inputData.type,
      height: inputData.height,
    });
  }, [inputData.color, inputData.type, inputData.height]);

  useEffect(() => {
    const filteredPanels = isInArray(panels, '@id', inputData.panelIRI);
    if (filteredPanels?.length === 0) {
      handlePanelSelectChange();
      setAddedProducts([]);
      setInputDataToSend('accessoriesFlat', []);
    }
  }, [panels, inputData.panelIRI]);

  useEffect(() => {
    const filteredPosts = isInArray(posts, '@id', inputData.postIRI);
    if (filteredPosts?.length === 0) {
      handlePostSelectChange();
      setAddedProducts([]);
      setInputDataToSend('accessoriesFlat', []);
    }
  }, [posts, inputData.postIRI]);

  return (
    <>
      <form className={classes.root} autoComplete="off" onSubmit={handleCalculateButtonSubmit}>
        <Box className={classes.inputBox}>
          <StableSection />
          <ColorSection />
          <TypeSection />
          <HeightSection />
          <PanelSection
            panelsInput={panelsInput}
            setPanelsInput={setPanelsInput}
            handlePanelSelectChange={handlePanelSelectChange}
          />
          <PostSection
            postsInput={postsInput}
            setPostsInput={setPostsInput}
            handlePostSelectChange={handlePostSelectChange}
          />
          {cornerPosts['hydra:member']?.length > 0 && inputData.corners > 0
          && <CornerPostSection />}
          {cornerPosts['hydra:member']
          && inputData.corners > 0
          && cornerPosts['hydra:member'].length === 0
          && <CheckboxSection />}
        </Box>
        <AccessorySection />
        <Box className={classes.resultsWrapper}>
          <Button
            className={classes.buttonCalculate}
            variant="contained"
            color="secondary"
            type="submit"
          >
            Calculate
          </Button>
          <ResetButton />
          <Loader target="calculate" size={32} backdrop centeredX centeredY />
          {necessaryProductsQuantity.items?.length > 0 && <ResultsSection />}
        </Box>
      </form>
    </>
  );
}

OrderForm.propTypes = {
  inputData: inputDataCustom.isRequired,
  setInputDataToSend: PropTypes.func.isRequired,
  panels: PropTypes.objectOf(PropTypes.any).isRequired,
  posts: PropTypes.objectOf(PropTypes.any).isRequired,
  cornerPosts: PropTypes.objectOf(PropTypes.any).isRequired,
  setAddedProducts: PropTypes.func.isRequired,
  fetchProductsBySection: PropTypes.func.isRequired,
  calculateProductsQuantity: PropTypes.func.isRequired,
  setValidation: PropTypes.func.isRequired,
  fetchFilters: PropTypes.func.isRequired,
  necessaryProductsQuantity: PropTypes.objectOf(PropTypes.any).isRequired,
};
